import React, {
  useCallback,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useMemo,
  useEffect,
} from "react";
import { Tabs } from "antd";
import styles from "./index.module.scss";
import RedeemPointsItem from "./components/RedeemPointsItem";
import { AvailableGiftCardItem } from "../../constants";
import { availableGiftCards, availableDiscountCards } from "../../constants";
import classNames from "classnames";
import { sendMessageToParent } from "../../utils";
import { IDiscountItem } from "../../types";
import DiscountCardItem from "./components/DiscountCardItem";
import CouponsItem from "./components/CouponsItem";
import GiftCardItem, { IGiftCardItem } from "./components/GiftCardItem";

export interface PointItem {
  available_points: number;
  customer_id: number;
  frozen_points: number;
  id: number;
  total_points: number;
  total_save_amount: number;
}

export interface DiscountItem {
  available_points: number;
  customer_id: number;
  frozen_points: number;
  id: number;
  total_points: number;
  total_save_amount: number;
}

export interface CouponItem {
  create_time: string;
  discount_code_type: number;
  id: number;
  percentage: number;
  shopify_customer_id: number;
  shopify_discount_code: string;
  shopify_discount_code_id: number;
  source_type: number;
  status: number;
  update_time: string;
}

interface PointsAndCouponsProps {
  points: PointItem[];
  coupons: CouponItem[];
  discountCards: IDiscountItem[];
  giftCards: IGiftCardItem[];
  customerId: number;
  onRedeem?: (data: AvailableGiftCardItem) => void;
}

// 定义暴露给父组件的方法接口
export interface PointsAndCouponsRef {
  scrollIntoView: () => void;
  switchTab: (tab: string) => void;
}

const PointsAndCoupons = forwardRef<PointsAndCouponsRef, PointsAndCouponsProps>(
  (
    { points, coupons, discountCards, giftCards, onRedeem, customerId },
    ref
  ) => {
    const [activeTab, setActiveTab] = useState<string>("points");
    const [showGiftCardNotification, setShowGiftCardNotification] =
      useState<boolean>(false);

    const notificationKey = useMemo(() => `showGiftCardNotification`, []);

    // 创建内部 ref 来引用 DOM 元素
    const innerRef = useRef<HTMLDivElement>(null);
    const pointsActivitiesRef = useRef<HTMLDivElement>(null);
    const { available_points, frozen_points, total_points } = points[0] || {};

    console.log(">>>> points:", points);

    const GiftCardEmpty = useCallback(() => {
      return (
        <div className={styles.empty}>
          <p>You may redeem points for your rewards.</p>
        </div>
      );
    }, []);

    // const CouponsEmpty = useCallback(() => {
    //   const _styles = {
    //     fontSize: "16px",
    //     fontWeight: "bold",
    //     color: "rgb(118, 75, 57)",
    //   };

    //   return (
    //     <div className={styles.empty}>
    //       <p>
    //         Your coupons will be there in <b style={_styles}>5 minutes</b>.
    //       </p>
    //     </div>
    //   );
    // }, []);

    const TipText = useCallback(() => {
      const textStyle = {
        fontSize: "16px",
        fontWeight: "bold",
        color: "#764B39",
      };

      // 没有任何积分
      if (!total_points)
        return <span>Complete your purchase to activate points!</span>;
      // 有积分，但是没有可用的积分
      if (available_points === 0 && frozen_points > 0)
        return (
          <span>
            <span style={textStyle}>{frozen_points}</span> points in transit.
            Unlock at delivery!
          </span>
        );
      // 有积分，并且有可用的积分
      if (available_points > 0 && frozen_points > 0)
        return (
          <span>
            <span style={textStyle}>{total_points}</span> points total:{" "}
            <span style={textStyle}>{frozen_points}</span> points in transit.{" "}
            {available_points} points redeemable.
          </span>
        );
      // 有积分，并且都是可用积分
      if (available_points > 0 && frozen_points === 0)
        return (
          <span>
            Your total points are <span style={textStyle}>{total_points}</span>.
            Ready to redeem.
          </span>
        );

      return <span></span>;
    }, [available_points, frozen_points, total_points]);

    const handleRedeem = useCallback(
      (data: AvailableGiftCardItem) => {
        onRedeem?.(data);
        localStorage.setItem(notificationKey, "true");
        setShowGiftCardNotification(true);
      },
      [onRedeem, notificationKey]
    );

    const items = useMemo(
      () => [
        {
          key: "points",
          label: <div data-element-id="tabs-redeem-points">Redeem Points</div>,
          children: (
            <>
              <div className={styles.noticeText}>
                <TipText />
              </div>
              <div className={styles.giftCardsList}>
                {availableDiscountCards.map((card, index) => (
                  <RedeemPointsItem
                    key={card.id}
                    index={index}
                    data={card}
                    userPoints={available_points}
                    onRedeem={handleRedeem}
                    customerId={customerId}
                  />
                ))}
              </div>
            </>
          ),
        },
        {
          key: "rewards",
          label: (
            <div
              className={styles.discountCardLabel}
              data-element-id="tabs-discount-card"
            >
              Rewards
              {showGiftCardNotification && (
                <span className={styles.notificationPoint}></span>
              )}
            </div>
          ),
          children: (
            <div
              className={classNames(
                styles.tabContent,
                discountCards.length + giftCards.length === 0
                  ? styles.emptyLayout
                  : ""
              )}
            >
              <>
                {/* 折扣券、免邮券 */}
                {discountCards.length > 0
                  ? discountCards.map((card) => {
                      if (card.discount_code_type === 2) {
                        return <CouponsItem key={card.id} data={card} />;
                      }

                      return (
                        <DiscountCardItem
                          key={card.id}
                          data={card}
                          availableGiftCards={availableDiscountCards}
                        />
                      );
                    })
                  : null}

                {/* gift card */}
                {giftCards.length > 0
                  ? giftCards.map((card) => (
                      <GiftCardItem
                        key={card.id}
                        data={card}
                        availableGiftCards={availableGiftCards}
                      />
                    ))
                  : null}

                {/* 没有数据时展示 */}
                {giftCards.length === 0 && discountCards.length === 0 && (
                  <GiftCardEmpty />
                )}
              </>
            </div>
          ),
        },
      ],
      [
        discountCards,
        giftCards,
        available_points,
        showGiftCardNotification,
        customerId,
        GiftCardEmpty,
        TipText,
        handleRedeem,
      ]
    );

    const handleChangeTab = useCallback(
      (key: string) => {
        console.log(">>>> key:", key);

        sendMessageToParent({
          type: "send-datac",
          data: {
            elementId: `tabs-${key}`,
          },
        });

        setActiveTab(key);

        // 切换到 giftCard 的时候，移除 notificationKey
        if (key === "rewards") {
          localStorage.removeItem(notificationKey);
          setShowGiftCardNotification(false);
        }

        // 每次切换的时候，都把主页上的 toast 隐藏
        sendMessageToParent({
          type: "member-toast-hide",
        });
      },
      [notificationKey]
    );

    useImperativeHandle(ref, () => ({
      scrollIntoView: () => {
        setTimeout(() => {
          innerRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 200);
      },
      getElementPosition: () => {
        const rect = innerRef.current?.getBoundingClientRect();
        return {
          top: (rect?.top || 0) + window.pageYOffset + 90 - 45, // 减去 iframe 前面内容的高度 90px
          left: (rect?.left || 0) + window.pageXOffset, // 相对于文档左侧的 X 坐标
          width: rect?.width || 0,
          height: rect?.height || 0,
        };
      },
      getPointsActivitiesPosition: () => {
        const rect = pointsActivitiesRef.current?.getBoundingClientRect();
        return {
          top: (rect?.top || 0) + window.pageYOffset + 90, // 减去 iframe 前面内容的高度 90px
          left: (rect?.left || 0) + window.pageXOffset,
          width: rect?.width || 0,
          height: rect?.height || 0,
        };
      },
      switchTab: (tab: string) => {
        console.log(">>>> tab:", tab);
        setActiveTab(tab);
      },
    }));

    // 初始化 giftCard 的 notificationPoint 状态
    useEffect(() => {
      const isShowNotification = localStorage.getItem(notificationKey);
      setShowGiftCardNotification(!!isShowNotification);
    }, [notificationKey]);

    return (
      <div ref={innerRef} className={styles.pointsAndCoupons}>
        <Tabs
          defaultActiveKey="points"
          activeKey={activeTab}
          className={styles.antTabs}
          items={items}
          onChange={(key) => handleChangeTab(key)}
        />
      </div>
    );
  }
);

export default PointsAndCoupons;
